import React, { createContext, useContext, useState, useEffect, useMemo } from 'react'
import axios from 'axios'
import { resolveLanguageCodes, parseDefaultLanguageCode } from '../constants/preferredLanguages'
import { isSessionTimeoutError, emitSessionTimeout, clearSessionExpiredFlag, isAuthFailureError, isTransientAuthError } from '../utils/sessionTimeout'

const LAST_ACTIVITY_KEY = 'rs_last_activity'

const AuthContext = createContext()

function parsePreferredLanguages(user) {
  if (!user) return ['en']
  if (Array.isArray(user.preferred_languages) && user.preferred_languages.length) {
    return resolveLanguageCodes(user.preferred_languages, 3)
  }
  try {
    const meta = JSON.parse(user.registration_notes || '{}')
    if (meta?.languages?.length) return resolveLanguageCodes(meta.languages, 3)
  } catch {
    /* ignore */
  }
  return ['en']
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const token = localStorage.getItem('token')
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
      fetchUser().finally(() => setLoading(false))
    } else {
      setLoading(false)
    }
  }, [])

  const fetchUser = async (attempt = 0) => {
    try {
      const res = await axios.get('/api/auth/me')
      setUser(res.data)
      sessionStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()))
      return res.data
    } catch (err) {
      if (isSessionTimeoutError(err)) {
        emitSessionTimeout()
        return null
      }
      if (isAuthFailureError(err)) {
        logout()
        return null
      }
      if (isTransientAuthError(err) && attempt < 2) {
        await new Promise((resolve) => setTimeout(resolve, 600 * (attempt + 1)))
        return fetchUser(attempt + 1)
      }
      return null
    }
  }

  const login = async (email, password, extras = {}) => {
    const res = await axios.post('/api/auth/login', { email, password, ...extras })
    const { token, user } = res.data
    localStorage.setItem('token', token)
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
    sessionStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()))
    setUser(user)
    return user
  }

  const logout = () => {
    localStorage.removeItem('token')
    sessionStorage.removeItem(LAST_ACTIVITY_KEY)
    clearSessionExpiredFlag()
    delete axios.defaults.headers.common['Authorization']
    setUser(null)
  }

  const isPaid = !!(user?.is_paid || user?.company_paid)
  const isCompanyPaid = !!user?.company_paid
  const role = user?.role || ''
  const isAdmin = role === 'Admin'
  const preferredLanguages = useMemo(() => parsePreferredLanguages(user), [user])
  const defaultLanguage = useMemo(() => parseDefaultLanguageCode(user), [user])

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        loading,
        isPaid,
        isCompanyPaid,
        preferredLanguages,
        defaultLanguage,
        role,
        isAdmin,
        refreshUser: fetchUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
